import {
  createSlice,
  createAsyncThunk,
  createSelector,
  createAction,
} from '@reduxjs/toolkit';
import axios from 'axios';
import { RootState } from 'state/store';
import {
  TaskListElement,
  TaskListElementPermissions,
  TaskListFilters,
  TasksListTableSorting,
} from 'types/store/tasksList';
import { Status } from 'utils/customTypes';
import { SLICE_STATUS } from 'utils/constants';
import {
  CENTRALIZED_TASKS_TABLE_TABS,
  DEFAULT_TASK_ELEMENT_PERMISSIONS,
} from 'utils/constants/centralizedTasks';
import { fetchFilterSettingByType } from 'state/Settings/Filters/FiltersSlice';
import { fetchTasksList } from 'api/tasksList';
import {
  normalizeFiltersSettingsVersions,
  getFetchCentralizedTasksListQueryParams,
} from '../helpers';
import {
  setTasksAssignees,
  setTasksProjects,
  selectAssigneesListByIds,
  selectTaskProjectById,
} from '../allTasks/allTasksSlice';

interface MyTasksSliceState {
  tasks: TaskListElement[];
  status: Status;
  filters: TaskListFilters;
  sorting: TasksListTableSorting;
  links: {
    next: string | null;
  };
  totalCount?: number;
  permissions?: Record<string, TaskListElementPermissions>;
}

const initialState: MyTasksSliceState = {
  tasks: [],
  status: SLICE_STATUS.IDLE,
  filters: {},
  sorting: {
    sortBy: 'name',
    order: 'desc',
  },
  links: {
    next: null,
  },
};

export interface FetchMyTasksParams {
  userId: string;
  fetchNext?: boolean;
  includeTotalCount?: boolean;
}

export const fetchMyTasks = createAsyncThunk(
  'tasksList/teamTasks/fetchMyTasks',
  async (
    {
      userId,
      fetchNext = false,
      includeTotalCount = false,
    }: FetchMyTasksParams,
    { rejectWithValue, dispatch, getState }
  ) => {
    try {
      const state = getState() as RootState;
      let nextLink = null;
      const filters = state.tasksList.myTasks.filters;
      const sorting = state.tasksList.myTasks.sorting;
      if (fetchNext) {
        nextLink = state.tasksList.myTasks.links.next;
      }
      const queryParams = getFetchCentralizedTasksListQueryParams(
        { userId, nextLink, includeTotalCount },
        filters,
        sorting
      );
      const response = await fetchTasksList(queryParams);
      dispatch(setTasksAssignees(response.assignees));
      dispatch(setTasksProjects(response.projects));
      return {
        tasks: response.tasks,
        links: response.links,
        totalCount: response.totalCount,
        fetchNext,
        permissions: response.permissions,
      };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data);
      }
      return rejectWithValue('Failed to fetch user tasks');
    }
  }
);

export const updateTaskInList = createAction<{
  taskId: string;
  updatedTask: Partial<TaskListElement>;
}>('tasksList/myTasks/updateTaskInList');

const myTasksSlice = createSlice({
  name: 'tasksList/myTasks',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = {
        search: state.filters.search,
        ...action.payload,
      };
    },
    setSorting: (state, action) => {
      state.sorting = action.payload;
    },
    setSearchFilter: (state, action) => {
      state.filters.search = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMyTasks.pending, (state) => {
        state.status = SLICE_STATUS.LOADING;
      })
      .addCase(fetchMyTasks.fulfilled, (state, action) => {
        state.status = SLICE_STATUS.IDLE;
        if (action.payload) {
          const fetchNext = action.payload.fetchNext;
          let updatedTasks = action.payload.tasks;
          let permissions = action.payload.permissions;
          if (fetchNext) {
            updatedTasks = state.tasks.concat(action.payload.tasks);
            permissions = {
              ...state.permissions,
              ...action.payload.permissions,
            };
          }
          state.tasks = updatedTasks;
          state.links = action.payload.links;
          state.permissions = permissions;
          if (action.payload.totalCount !== undefined) {
            state.totalCount = action.payload.totalCount;
          }
        }
      })
      .addCase(fetchMyTasks.rejected, (state) => {
        state.status = SLICE_STATUS.FAILED;
      })
      .addCase(fetchFilterSettingByType.fulfilled, (state, action) => {
        if (
          action.payload &&
          action.payload.filter_type === CENTRALIZED_TASKS_TABLE_TABS.MY_TASKS
        ) {
          state.filters = normalizeFiltersSettingsVersions(
            action.payload.filter_settings
          );
        }
      })
      .addCase(updateTaskInList, (state, action) => {
        if (action.payload) {
          const { taskId, updatedTask } = action.payload;
          const taskIndex = state.tasks.findIndex((task) => task.id === taskId);
          if (taskIndex !== -1) {
            state.tasks[taskIndex] = {
              ...state.tasks[taskIndex],
              ...updatedTask,
            };
          }
        }
      });
  },
});

const selectTasks = (state: RootState) => state.tasksList.myTasks.tasks;
const selectTasksPermissions = (state: RootState) =>
  state.tasksList.myTasks.permissions ?? {};
export const selectFetchMyTasksStatus = (state: RootState) =>
  state.tasksList.myTasks.status;
export const selectMyTasks = createSelector(
  [(state: RootState) => state, selectTasks, selectTasksPermissions],
  (
    state: RootState,
    tasks: MyTasksSliceState['tasks'],
    permissions: MyTasksSliceState['permissions']
  ) => {
    return tasks.map((task) => {
      return {
        ...task,
        assignees: selectAssigneesListByIds(state, task.assignees),
        project: selectTaskProjectById(state, task.projectId),
        permissions: permissions
          ? permissions[task.id]
          : DEFAULT_TASK_ELEMENT_PERMISSIONS,
      };
    });
  }
);
export const selectMyTasksTableSorting = (state: RootState) =>
  state.tasksList.myTasks.sorting;

export const selectMyTasksFilters = (state: RootState) =>
  state.tasksList.myTasks.filters;

export const selectCanFetchMoreMyTasks = (state: RootState) =>
  !!state.tasksList.myTasks.links.next;

export const selectMyTasksTotalCount = (state: RootState) =>
  state.tasksList.myTasks.totalCount;

export const { setFilters, setSorting, setSearchFilter } = myTasksSlice.actions;

export default myTasksSlice.reducer;
